import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronRight, Clock, FolderOpen, Shield, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MatterUploadPage() {
  const { matterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [matters, setMatters] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (matterId) {
          const [matterRes, batchRes] = await Promise.all([
            ax.get(`/matters/${matterId}`),
            ax.get(`/matters/${matterId}/upload-batches`),
          ]);
          setMatter(matterRes.data);
          setBatches(batchRes.data);
        } else {
          const res = await ax.get('/matters');
          setMatters(res.data);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load upload path');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [matterId]);

  const openSyncPortal = async () => {
    if (!matterId) return;
    setOpening(true);
    try {
      const res = await ax.post(`/matters/${matterId}/upload-batches`, {});
      setBatches((prev) => [res.data, ...prev]);
      window.open(res.data.sync_portal_url, '_blank', 'noopener,noreferrer');
      toast.success('Secure Sync intake portal opened');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open secure upload');
    } finally {
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#0B1F3F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!matterId) {
    return (
      <div className="min-h-screen bg-[#F5F3EF]">
        <header className="bg-white border-b border-[#D4D4D0]">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
            </Link>
            <Link to="/dashboard/uploads" className="text-sm text-[#64748B] hover:text-[#0B1F3F] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
          <div className="space-y-3">
            <h1 className="font-heading text-4xl font-semibold text-[#0B1F3F]">Upload Additional Records</h1>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
            <p className="text-[#64748B] max-w-3xl leading-relaxed">
              Select the existing matter in your account before opening the secure Sync upload path. Uploads are tied to a specific matter and should not be sent through a generic intake path.
            </p>
          </div>

          {matters.length === 0 ? (
            <div className="bg-white border border-[#D4D4D0] rounded-sm p-8 text-center space-y-3">
              <FolderOpen className="w-12 h-12 text-[#D4D4D0] mx-auto" />
              <p className="text-[#0B1F3F] font-medium">No matters are available in this account.</p>
              <p className="text-sm text-[#64748B]">Start intake first to create a matter before uploading additional records.</p>
              <div className="pt-2">
                <Button asChild className="btn-primary-varfacts rounded-sm px-6 py-3">
                  <Link to="/intake">Start Intake</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {matters.map((item) => (
                <button
                  key={item.matter_id}
                  onClick={() => navigate(`/matters/${item.matter_id}/upload?source=existing`)}
                  className="bg-white border border-[#D4D4D0] rounded-sm p-6 text-left hover:border-[#0B1F3F] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <p className="text-[#0B1F3F] font-semibold">{item.matter_name}</p>
                      <p className="text-xs text-[#94A3B8]">{item.matter_reference}</p>
                      <p className="text-sm text-[#64748B]">{item.matter_description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#C5A55A] flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const fromIntake = new URLSearchParams(location.search).get('source') === 'intake';

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      <header className="bg-white border-b border-[#D4D4D0]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
          </Link>
          <Link to={fromIntake ? '/dashboard/matters' : '/upload-records'} className="text-sm text-[#64748B] hover:text-[#0B1F3F] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        <div className="space-y-3">
          <h1 className="font-heading text-4xl font-semibold text-[#0B1F3F]">
            {fromIntake ? 'Matter Intake Submitted' : 'Matter Upload Path'}
          </h1>
          <div className="w-16 h-0.5 bg-[#C5A55A]" />
          <p className="text-[#64748B] max-w-3xl leading-relaxed">
            This upload path is tied to a specific matter inside your account. Use the secure Sync intake portal below to transfer records for this matter only.
          </p>
        </div>

        <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter</p>
              <p className="text-[#0B1F3F] font-semibold text-lg">{matter?.matter_name}</p>
              <p className="text-xs text-[#94A3B8]">{matter?.matter_reference}</p>
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Account Matter
            </span>
          </div>
          <p className="text-sm text-[#64748B]">{matter?.matter_description}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Client</p>
              <p className="text-[#0B1F3F] mt-1">{matter?.client_name}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Case Type</p>
              <p className="text-[#0B1F3F] mt-1">{matter?.case_type || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Created</p>
              <p className="text-[#0B1F3F] mt-1">{formatDate(matter?.created_at)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#0B1F3F]" />
            <h2 className="font-heading text-xl font-semibold text-[#0B1F3F]">Secure Sync Upload</h2>
          </div>
          <p className="text-sm text-[#64748B] leading-relaxed">
            Sync remains the only file-transfer method. Opening the portal registers a new upload batch for this matter and sends you to the secure destination tied to this matter record.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={openSyncPortal} disabled={opening} className="btn-primary-varfacts rounded-sm px-6 py-3 h-12">
              <Upload className="w-4 h-4 mr-2" />
              {opening ? 'Opening...' : 'Open Secure Intake Portal'}
            </Button>
            <Button asChild variant="outline" className="btn-secondary-varfacts rounded-sm px-6 py-3 h-12">
              <Link to="/dashboard/uploads">View Upload Batches</Link>
            </Button>
          </div>
          <p className="text-xs text-[#94A3B8]">
            Sensitive records should not be sent by ordinary email.
          </p>
        </div>

        <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
          <div className="p-5 border-b border-[#D4D4D0]">
            <h3 className="font-heading text-lg font-semibold text-[#0B1F3F]">Upload Batch History</h3>
          </div>
          {batches.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94A3B8]">No upload batches recorded yet</div>
          ) : (
            <table className="w-full data-table">
              <thead>
                <tr className="text-left">
                  <th>Batch</th>
                  <th>Method</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.batch_id}>
                    <td className="text-[#0B1F3F] font-medium">{batch.batch_id}</td>
                    <td className="text-[#64748B] text-xs uppercase">{batch.transfer_method}</td>
                    <td className="text-[#94A3B8] text-xs flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      {formatDate(batch.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
